import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, ArrowLeft, FileText, Save, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";

export default function FacturaDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [clients, setClients] = useState([]);
  const [pdfPath, setPdfPath] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/invoices/${id}`);
      const cs = await api.get("/clients");
      setInv(r.data); setClients(cs.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const totals = useMemo(() => {
    if (!inv) return { subtotal: 0, vat: 0, total: 0 };
    const subtotal = (inv.lines || []).reduce((s, l) => s + Number(l.amount || 0), 0);
    const vat = subtotal * Number(inv.vat_rate || 0) / 100;
    return { subtotal, vat, total: subtotal + vat };
  }, [inv]);

  const setLine = (idx, patch) => setInv((x) => {
    const lines = [...x.lines]; lines[idx] = { ...lines[idx], ...patch };
    return { ...x, lines };
  });
  const addLine = () => setInv((x) => ({ ...x, lines: [...(x.lines || []), { description: "", amount: 0 }] }));
  const removeLine = (idx) => setInv((x) => ({ ...x, lines: x.lines.filter((_, i) => i !== idx) }));

  const save = async () => {
    try { await api.put(`/invoices/${id}`, inv); toast.success("Factura guardada"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePaid = async () => {
    try {
      const r = await api.post(`/invoices/${id}/mark-paid`);
      setInv((x) => ({ ...x, paid: r.data.paid, paid_at: r.data.paid_at }));
      toast.success(r.data.paid ? "Factura marcada como cobrada" : "Marca de cobro retirada");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!inv) return <div className="text-zinc-500">Cargando…</div>;
  const client = clients.find(c => c.id === inv.client_id);

  return (
    <div className="space-y-8" data-testid="factura-detail">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => nav("/facturas")} className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-zinc-900 mb-2"><ArrowLeft size={12} /> Volver</button>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="section-title">Factura</h1>
            <Label htmlFor="invoice-number" className="text-xs text-zinc-500 whitespace-nowrap">Nº factura</Label>
            <Input
              id="invoice-number"
              className="h-8 text-sm font-mono w-[260px]"
              value={inv.number || ""}
              onChange={(e) => setInv({ ...inv, number: e.target.value })}
              placeholder="Editable completo"
              autoComplete="off"
              aria-label="Número de factura editable completo"
              data-testid="invoice-number"
            />
          </div>
          <div className="flex items-center gap-3 mt-2">
            <Label htmlFor="inv-date" className="text-xs text-zinc-500">Fecha</Label>
            <Input
              id="inv-date"
              type="date"
              className="h-8 w-[160px] text-sm"
              value={(inv.date || "").slice(0, 10)}
              onChange={(e) => {
                const v = e.target.value;
                setInv({ ...inv, date: v ? `${v}T12:00:00+00:00` : "" });
              }}
              data-testid="invoice-date"
            />
            <Label className="text-xs text-zinc-500">Cliente</Label>
            <Select
              value={inv.client_id || "none"}
              onValueChange={(v) => setInv({ ...inv, client_id: v === "none" ? null : v })}
            >
              <SelectTrigger data-testid="invoice-client" className="h-8 w-[220px] text-sm">
                <SelectValue placeholder="Sin asignar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Sin cliente —</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.cif ? ` (${c.cif})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={inv.paid ? "default" : "outline"}
            onClick={togglePaid}
            data-testid="btn-mark-paid"
            className={inv.paid ? "bg-green-600 hover:bg-green-700 text-white" : ""}
            title={inv.paid_at ? `Cobrada el ${(inv.paid_at || "").slice(0, 10)}` : ""}
          >
            {inv.paid ? <CheckCircle2 size={14} className="mr-1" /> : <Circle size={14} className="mr-1" />}
            {inv.paid ? `Cobrada (${(inv.paid_at || "").slice(0, 10)})` : "Marcar cobrada"}
          </Button>
          <Button variant="outline" onClick={save} data-testid="btn-save-invoice"><Save size={14} className="mr-1" />Guardar</Button>
          <Button onClick={() => setPdfPath(`/invoices/${id}/pdf`)} data-testid="btn-invoice-pdf"><FileText size={14} className="mr-1" />PDF</Button>
          {inv.quote_id && (
            <Button variant="outline" onClick={() => nav(`/presupuestos/${inv.quote_id}`)} data-testid="btn-go-quote">Ver presupuesto</Button>
          )}
        </div>
      </div>

      <div className="page-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Líneas</div>
          <Button variant="outline" size="sm" onClick={addLine} data-testid="btn-add-line"><Plus size={12} className="mr-1" />Añadir línea</Button>
        </div>
        <div className="space-y-2">
          {(inv.lines || []).map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-line-${i}`}>
              <div className="col-span-9">
                <Input
                  type="text"
                  value={l.description || ""}
                  onChange={(e) => setLine(i, { description: e.target.value })}
                  placeholder="Descripción de la línea"
                  aria-label="Descripción de línea"
                  data-testid={`invoice-line-description-${i}`}
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  step="0.01"
                  value={l.amount}
                  onChange={(e) => setLine(i, { amount: parseFloat(e.target.value || "0") })}
                  aria-label="Importe de línea"
                  data-testid={`invoice-line-amount-${i}`}
                />
              </div>
              <div className="col-span-1 text-right">
                <button onClick={() => removeLine(i)} className="text-zinc-500 hover:text-red-600 p-1"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {(inv.lines || []).length === 0 && <div className="text-sm text-zinc-400 py-6 text-center">Sin líneas. Añade alguna.</div>}
        </div>

        <div className="grid grid-cols-2 mt-6 gap-6">
          <div>
            <Label className="text-xs">IVA (%)</Label>
            <Input type="number" step="0.5" value={inv.vat_rate} onChange={(e) => setInv({ ...inv, vat_rate: parseFloat(e.target.value || "0") })} />
            <Label className="text-xs mt-3 block">IBAN (forma de pago)</Label>
            <Input value={inv.iban || ""} onChange={(e) => setInv({ ...inv, iban: e.target.value })} />
            <Label className="text-xs mt-3 block">Notas</Label>
            <Textarea rows={3} value={inv.notes || ""} onChange={(e) => setInv({ ...inv, notes: e.target.value })} />
          </div>
          <div className="text-sm">
            <div className="flex justify-between py-1"><span className="text-zinc-500">Subtotal</span><span>{totals.subtotal.toFixed(2)} €</span></div>
            <div className="flex justify-between py-1"><span className="text-zinc-500">IVA ({inv.vat_rate}%)</span><span>{totals.vat.toFixed(2)} €</span></div>
            <div className="flex justify-between font-display text-2xl border-t border-[var(--rule)] pt-3 mt-3">
              <span>Total</span><span data-testid="invoice-total">{totals.total.toFixed(2)} €</span>
            </div>
          </div>
        </div>
      </div>

      <PdfPreviewDialog path={pdfPath} filename={`factura_${inv.number}.pdf`} onClose={() => setPdfPath(null)} />
    </div>
  );
}
